// frontend/src/core/components/CustomVideoPlayer/CustomVideoPlayer.jsx

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { CButton, CSpinner } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilMediaPlay, cilMediaPause, cilVolumeHigh, cilVolumeOff, cilFullscreen } from "@coreui/icons";
import ReactPlayer from "react-player";

// Вспомогательная функция для форматирования времени
const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds < 0) return "00:00";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    if (h > 0) return `${h.toString().padStart(2, '0')}:${m}:${s}`;
    return `${m}:${s}`;
};

const CustomVideoPlayer = forwardRef(({ 
    url, 
    initialSeekTime, 
    seekTriggerId, 
    onProgress, 
    onReady,
    children,
    // Пропсы для синхронизации, как в Multi-мониторе
    externalPlaying, 
    onTogglePlay, 
    externalPlaybackRate, 
    onPlaybackRateChange 
}, ref) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  
  const [state, setState] = useState({
    playing: true,
    volume: 0.8,
    muted: false,
    played: 0,
    duration: 0,
    seeking: false,
    playbackRate: 1.0
  });

  const isPlaying = externalPlaying !== undefined ? externalPlaying : state.playing;
  const currentPlaybackRate = externalPlaybackRate !== undefined ? externalPlaybackRate : state.playbackRate;

  // Позволяем родительскому компоненту получить доступ к video-элементу
  useImperativeHandle(ref, () => ({
    getInternalPlayer: () => playerRef.current ? playerRef.current.getInternalPlayer() : null
  }));

  // Сброс состояния при смене видео
  useEffect(() => {
    setIsReady(false);
    setState(s => ({ ...s, played: 0, duration: 0, playing: true }));
  }, [url]);

  // Перемотка при клике на таймлайн
  useEffect(() => {
    if (isReady && playerRef.current && initialSeekTime >= 0) {
      if (onTogglePlay) onTogglePlay(true);
      else setState(s => ({ ...s, playing: true }));
      playerRef.current.seekTo(initialSeekTime, "seconds");
    }
  }, [seekTriggerId, initialSeekTime, isReady, onTogglePlay]);

  const handlePlayPause = () => {
    if (onTogglePlay) onTogglePlay(!isPlaying);
    else setState(s => ({ ...s, playing: !s.playing }));
  };
  
  const handleSpeedChange = () => {
    const rates = [0.1, 0.25, 0.5, 1.0];
    const nextRate = rates[(rates.indexOf(currentPlaybackRate) + 1) % rates.length];
    if (onPlaybackRateChange) onPlaybackRateChange(nextRate);
    else setState(s => ({ ...s, playbackRate: nextRate }));
  };

  // --- ИСПРАВЛЕННАЯ ЛОГИКА ПЕРЕМОТКИ ---
  const handleSeekMouseDown = () => setState(s => ({ ...s, seeking: true }));
  const handleSeekChange = (e) => setState(s => ({ ...s, played: parseFloat(e.target.value) }));
  const handleSeekMouseUp = (e) => playerRef.current.seekTo(parseFloat(e.target.value));
  const handleSeekComplete = () => setState(s => ({ ...s, seeking: false }));

  const handleClickFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.() || el.webkitRequestFullscreen?.();
    else document.exitFullscreen?.() || document.webkitExitFullscreen?.();
  };

  const currentTime = state.duration * state.played;

  return (
    <div ref={containerRef} className="position-relative w-100 h-100 bg-black d-flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
      {!isReady && <div className="position-absolute" style={{ zIndex: 10 }}><CSpinner color="light" /></div>}
      
      <ReactPlayer
        ref={playerRef}
        className="react-player"
        width="100%"
        height="100%"
        url={url}
        playing={isPlaying}
        playbackRate={currentPlaybackRate}
        volume={state.volume}
        muted={state.muted}
        progressInterval={100}
        onReady={(p) => {
          setIsReady(true);
          if (onReady) onReady(p.getInternalPlayer());
        }}
        onProgress={(p) => {
          if (!state.seeking) setState(s => ({ ...s, played: p.played }));
          if (onProgress) onProgress(p.playedSeconds);
        }}
        onDuration={(d) => setState(s => ({ ...s, duration: d }))}
        onEnded={() => { if (onTogglePlay) onTogglePlay(false); else setState(s => ({ ...s, playing: false })); }}
        onSeek={handleSeekComplete} // Ключевое исправление
        config={{ file: { attributes: { crossOrigin: "anonymous", playsInline: true } } }}
      />
      
      {isReady && children}
      
      <div className="p-2 w-100" style={{ position: "absolute", bottom: 0, left: 0, background: "linear-gradient(transparent, rgba(0,0,0,0.8))", display: "flex", flexDirection: "column", zIndex: 20, opacity: isReady ? 1 : 0.5 }}>
        <input 
          type="range" min={0} max={0.999999} step="any" value={state.played} 
          onMouseDown={handleSeekMouseDown} onChange={handleSeekChange} onMouseUp={handleSeekMouseUp}
          style={{ width: "100%", cursor: isReady ? "pointer" : "not-allowed", height: "4px", accentColor: "#321fdb" }}
          disabled={!isReady}
        />
        <div className="d-flex justify-content-between align-items-center text-white mt-1">
          <div className="d-flex align-items-center gap-3">
            <CButton color="link" className="text-white p-0" onClick={handlePlayPause} disabled={!isReady}>
              <CIcon icon={isPlaying ? cilMediaPause : cilMediaPlay} size="lg" />
            </CButton>
            <div className="d-flex align-items-center gap-1 d-none d-md-flex">
              <CButton color="link" className="text-white p-0" onClick={() => setState(s => ({ ...s, muted: !s.muted }))} disabled={!isReady}>
                <CIcon icon={state.muted || state.volume === 0 ? cilVolumeOff : cilVolumeHigh} />
              </CButton>
              <input type="range" min={0} max={1} step="any" value={state.muted ? 0 : state.volume} onChange={(e) => setState(s => ({ ...s, volume: parseFloat(e.target.value), muted: parseFloat(e.target.value) === 0 }))} disabled={!isReady} style={{ width: "60px", accentColor: "#fff", height: "3px" }}/>
            </div>
            <span className="small font-monospace">{formatDuration(currentTime)} / {formatDuration(state.duration)}</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            <CButton color="link" className="text-white p-0 text-decoration-none fw-bold" onClick={handleSpeedChange} disabled={!isReady} style={{ fontSize: '0.9rem', minWidth: '40px' }}>
              {currentPlaybackRate}x
            </CButton>
            <CButton color="link" className="text-white p-0" onClick={handleClickFullscreen} disabled={!isReady}>
              <CIcon icon={cilFullscreen} />
            </CButton>
          </div>
        </div>
      </div>
    </div>
  );
});

export default CustomVideoPlayer;